import React from "react";
import Joyride, { Step, CallBackProps, STATUS } from "react-joyride";
import { X } from "lucide-react";

interface EvaluateTutorialProps {
    run: boolean;
    setRun: (run: boolean) => void;
}

const steps: Step[] = [
    {
        target: "#tutorial-credits",
        title: "Credits Balance",
        content: "This is your credit balance. Each evaluation consumes 1 credit. You can upgrade to get more credits.",
        placement: "bottom",
        disableBeacon: true,
    },
    {
        target: "#tutorial-exam-select",
        title: "Select Exam",
        content: "First, select the exam category you want to evaluate your answer for. Currently, we support UPSC.",
        placement: "bottom",
    },
    {
        target: "#tutorial-paper-select",
        title: "Select Paper",
        content: "Next, choose the specific paper or subject (e.g., GS1, GS2, Essay) that you've written.",
        placement: "bottom",
    },
    {
        target: "#tutorial-upload-zone",
        title: "Upload Answer Sheet",
        content: "Upload your completed answer sheet as a PDF or multiple images. You can drag and drop files here.",
        placement: "right",
    },
    {
        target: "#tutorial-scan-pages",
        title: "Scan with Camera",
        content: "Alternatively, use your device camera to scan pages directly. This is great for handwritten answers!",
        placement: "left",
    },
    {
        target: "#tutorial-evaluate-button",
        title: "Start Evaluation",
        content: "Once everything is set, click here to start the AI evaluation. Our engine will analyze your answer and provide detailed feedback.",
        placement: "top",
    },
];

const Tooltip = ({
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    isLastStep,
    size,
}: any) => {
    return (
        <div {...tooltipProps} className="bg-white/95 backdrop-blur-xl border border-gray-100 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] rounded-[24px] p-5 max-w-[300px] relative overflow-hidden group">
            {/* Premium Decorative elements */}
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-indigo-600/5 rounded-full blur-3xl group-hover:bg-indigo-600/10 transition-colors duration-1000" />

            <div className="relative z-10">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-1.5 mb-1">
                            <span className="h-0.5 w-6 bg-indigo-600 rounded-full" />
                            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-indigo-600/80">
                                Step {index + 1} of {size}
                            </span>
                        </div>
                        <h3 className="text-lg font-black text-gray-900 leading-tight tracking-tight">
                            {step.title}
                        </h3>
                    </div>
                    <button {...closeProps} className="p-1.5 hover:bg-gray-100 rounded-full transition-all hover:scale-110 active:scale-90">
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                <div className="text-gray-500 text-[13px] leading-relaxed mb-6 font-medium">
                    {step.content}
                </div>

                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-[40px]">
                        {index > 0 && (
                            <button
                                {...backProps}
                                className="text-[12px] font-bold text-gray-400 hover:text-indigo-600 transition-colors py-1"
                            >
                                Back
                            </button>
                        )}
                    </div>
                    <button
                        {...primaryProps}
                        className="flex-1 bg-gray-900 hover:bg-indigo-600 text-white font-bold py-2.5 px-6 rounded-xl shadow-lg shadow-indigo-100 transition-all active:scale-[0.96] flex items-center justify-center gap-2 group/btn text-[13px]"
                    >
                        <span>{isLastStep ? "Finish" : "Next"}</span>
                        {!isLastStep && (
                            <svg className="w-3.5 h-3.5 transform group-hover/btn:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const EvaluateTutorial: React.FC<EvaluateTutorialProps> = ({ run, setRun }) => {
    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, action } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status) || action === "close") {
            setRun(false);
        }
    };

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            hideCloseButton={false}
            run={run}
            scrollToFirstStep={false}
            showProgress={false}
            showSkipButton={false}
            tooltipComponent={Tooltip}
            steps={steps.map((step) => ({
                ...step,
                disableBeacon: true,
            }))}
            disableOverlayClose
            spotlightClicks={false}
            disableScrolling={true}
            styles={{
                options: {
                    zIndex: 10000,
                    overlayColor: "rgba(0, 0, 0, 0.4)",
                },
                spotlight: {
                    borderRadius: "24px",
                },
            }}
            floaterProps={{
                disableAnimation: false,
                offset: 15,
            }}
        />
    );
};

export default EvaluateTutorial;
